import React, { useMemo, useState, useEffect, useContext, useCallback } from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLPublicContactsService } from 'gw-capability-quoteandbind';
import { MLPortalStorageService } from 'ml-services-common-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import { MLDriverVehicleUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { platformMessages, MLTooltipComponent } from 'ml-components-platform-react';
import appConfig from 'app-config';

import metadata from './AdditionalInfoToPurchasePagePolChange.metadata.json5';
import messages from './AdditionalInfoToPurchasePagePolChange.messages';
import styles from './AdditionalInfoToPurchasePagePolChange.module.scss';

const {
    ML_STORAGE_KEY_PREVIOUS_PREMIUM
} = appConfig;

const AMPAdditionalInfoToPurchasePagePolChange = (props) => {
    const { wizardData, goNext, updateWizardData } = props;
    const { submissionVM: endorsementVM } = wizardData;

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader } = useAuthentication();
    const { onValidate, isComponentValid } = useValidation('AdditionalInformationToPurchasePage');
    const { refreshData } = useDataRefresh();

    const [showErrors, setShowErrors] = useState(false);

    const {
        clearBlockAlerts,
        addErrorAlert,
        clearAlerts
    } = MLNotificationBlockUtil();

    const {
        showProgressDialog,
    } = MLModalService()

    const {
        hasPALeaseLoanCovButChangedOwnership,
        isFinanceWithoutPALeaseLoanCovButNoLienHolder,
        isLeaseButNoLessor,
        isOwnedButWithLienHolderLessor,
        validateVehiclesSerialNumber
    } = MLDriverVehicleUtil();

    const isPrimaryDriver = (driverVM) => {
        return driverVM.isPolicyHolder.value;
    };

    const isRatedDriver = (driverVM) => {
        return driverVM.type_ML.value.code === 'rated';
    };

    const isExcludedDriver = (driverVM) => {
        return driverVM.type_ML.value.code === 'excluded';
    };

    const isHouseholdDriver = (driverVM) => {
        return driverVM.type_ML.value.code === 'household';
    };

    const isInGA = (addressVM) => {
        return addressVM.state.value.code === 'GA';
    };

    const isInCO = (addressVM) => {
        return addressVM.state.value.code === 'CO';
    };

    const isInUT = (addressVM) => {
        return addressVM.state.value.code === 'UT';
    };

    const isInOR = (addressVM) => {
        return addressVM.state.value.code === 'OR';
    };

    const paModel = endorsementVM.lobData.personalAuto;
    const vehiclesVM = useMemo(() => {
        return paModel.coverables.vehicles.children.map((vehicleVM) => {
            vehicleVM.isVinRequired_ML.value = true;
            vehicleVM.isSerialNumberRequired_ML.value = true;
            vehicleVM.value.makeAddlInfoReq_Ext = true;
            vehicleVM.editMode_ML.value = true;
            return vehicleVM;
        });
    }, [paModel]);

    const vehiclesExistWithYearPre1981_ML = useCallback(() => {
        return vehiclesVM.some((vehicle) => {
            return vehicle.year.value < 1981;
        });
    }, [vehiclesVM]);

    const addressVM = endorsementVM.baseData.policyAddress;

    const driversVM = useMemo(() => {
        const getDriverSortPriority = (driverVM) => {
            if (isPrimaryDriver(driverVM)) {
                return 0;
            }
            switch (driverVM.type_ML.value.code) {
                case 'rated':
                    return 1;
                case 'household':
                    return 2;
                case 'excluded':
                    return 3;
                default:
                    return 9;
            }
        };
        const getSortedDriverListML = (unsortedDriversList) => {
            // Sorting by name first and then with Driver's priority to keep the order of the sorted array consistent.
            const nameSortedDriversList = unsortedDriversList.sort((driver1VM, driver2VM) => {
                const driverName1 = driver1VM.displayName.value ? driver1VM.displayName.value.toUpperCase() : '';
                const driverName2 = driver2VM.displayName.value ? driver2VM.displayName.value.toUpperCase() : '';

                if (driverName1 < driverName2) {
                    return -1;
                }

                if (driverName1 > driverName2) {
                    return 1;
                }

                return 0;
            });

            return nameSortedDriversList.sort((driver1VM, driver2VM) => {
                const driver1Priority = getDriverSortPriority(driver1VM);
                const driver2Priority = getDriverSortPriority(driver2VM);

                if (driver1Priority < driver2Priority) {
                    return -1;
                }

                if (driver1Priority > driver2Priority) {
                    return 1;
                }

                return 0;
            });
        };

        const driversToSort = [...paModel.coverables.drivers.children];
        const driversList = getSortedDriverListML(driversToSort);
        const sortedDriverList = [...driversList].map((driver) => driver.value);

        _.set(endorsementVM, `lobData.personalAuto.coverables.drivers.value`, sortedDriverList);

        return getSortedDriverListML(driversList);
    }, [paModel,endorsementVM]);

    const driversRequiresProof = useMemo(() => {
        return driversVM
            .filter((x) => x.requiresProof_ML.value === true)
            .map((driver) => {
                return Object.assign(driver, {
                    rerateForDriverNeeded: false
                })
            });
    }, [driversVM])

    const hasNamedNonOwner = paModel.value.hasNamedNonOwner();
    const ratedAndHouseholdCount = useMemo(() => {
        let count = 0;
        driversVM.forEach((driverVM) => {
            if (isRatedDriver(driverVM) || isHouseholdDriver(driverVM)) {
                count++;
            }
        });
        return count; 
    }, [driversVM])

    const primaryDriver = useMemo(() => {
        let aDriver = driversVM.find((driver) => isPrimaryDriver(driver));
        if (!aDriver) {
            aDriver = driversVM.find((driver) => driver.relationshipToInsured_ML.value.code === 'self');
        }
        return aDriver;
    }, [driversVM]);

    const primaryDriverEmail = _.get(primaryDriver, 'person.emailAddress_ML');

    const isSpouseDriver = (driverVM) => {
        return driverVM.relationshipToInsured_ML.value.code === 'spouse';
    };

    const primaryDriverSpouseIndex = _.findIndex(driversVM, (driver) => isSpouseDriver(driver));

    const [potentialContacts, setPotentialContacts] = useState([]);
    const [hidePage, setHidePage] = useState(true);

    const isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal = (vehicleVM) => {
        switch (vehicleVM.category_ML.value.code) {
            case 'trailer':
            case 'registered_golf_cart':
            case 'low_speed_vehicle':
            case 'Other_Personally_Registered':
                return true;
            default:
                return false;
        }
    };

    const getVehicleYearMakeName = (vehicleVM) => {
        return vehicleVM.value.getYearMakeNameML();
    };

    const getVehicleModelTrimName = (vehicleVM) => {
        const isVehiclePrivateOrClassic = () => {
            if (_.isNil(vehicleVM.type_ML.value)) {
                return false;
            }
            switch (vehicleVM.type_ML.value.code) {
                case 'private_passenger_autos_ml':
                case 'classic_ml':
                    return true;
                default:
                    return false;
            }
        };

        if (isVehiclePrivateOrClassic(vehicleVM)) {
            return `${vehicleVM.value.getModelNameML()} ${vehicleVM.value.getTrimNameML()}`;
        }
        return vehicleVM.value.getModelNameML();
    };

    const isFinancedOrLeased = (vehicleVM) => {
        return vehicleVM.value.financeCategory_ML === 'financed' || vehicleVM.value.financeCategory_ML === 'leased';
    };

    const removeAlertWithBlockID = (selectedAlerts, blockID) => {
        const index = selectedAlerts.findIndex((alert) => alert.block === blockID);
        if (index > -1) {
            selectedAlerts.splice(index, 1);
        }
    };

    const showDriverEmailAddress = (driverVM) => {
        if (isPrimaryDriver(driverVM)) {
            return true;
        }
        if (isSpouseDriver(driverVM) && isInCO(addressVM)) {
            return true;
        }
        if (isExcludedDriver(driverVM) && (isInGA(addressVM) || isInUT(addressVM) || isInOR(addressVM))) {
            return true;
        }
        return false;
    };

    const showSpouseEmailAddress = () => {
        const isSpouseHouseholdDriver = () => {
            return primaryDriverSpouseIndex !== -1 && isHouseholdDriver(driversVM[primaryDriverSpouseIndex]);
        }; 

        return isInCO(addressVM) && isSpouseHouseholdDriver();
    };

    const fetchORAvailableValues = (driverVM) => {
    
            const availableValues = driverVM?.reasonRequired_Ext?.aspects?.availableValues || [];
    
            const orCodes = [
                'driving_under_drugs',
                'other'
            ];
    
            const valuesToUse =
                driverVM?.requestingState_ML?.value?.code === 'OR'
                    ? availableValues.filter(v => orCodes.includes(v.code))
                    : availableValues;
    
            return valuesToUse?.map(v => ({
                code: v.code,
                name: translator({
                    id: v.name,
                    defaultMessage: v.name
                })
            }));
        };

    const validateSpouseEmail = (selectedAlerts) => {
        const primaryDriverSpouseEmail = primaryDriverSpouseIndex !== -1 ? driversVM[primaryDriverSpouseIndex].person.emailAddress_ML : undefined

        if (isInCO(addressVM) &&
            !!primaryDriverSpouseEmail &&
            primaryDriverSpouseEmail.value === primaryDriverEmail.value) {
            const errorMsgValidateSpouseEmail = messages.spouseEmailValidationMsg;
            selectedAlerts.push({type: 'error', message: errorMsgValidateSpouseEmail, block: 'spouseEmailValidation'});
            addErrorAlert(errorMsgValidateSpouseEmail, 'spouseEmailValidation');
            return false;
        }
        removeAlertWithBlockID(selectedAlerts, 'spouseEmailValidation');
        clearBlockAlerts('spouseEmailValidation');
        return true;
    };
    
    const setModifierEditMode = (editMode) => {
        endorsementVM.lobData.personalAuto.coverables.drivers.value.forEach((driver) => {
            if (driver.driverModifiers_ML && driver.driverModifiers_ML.modifiers && driver.driverModifiers_ML.modifiers.length) {
                driver.driverModifiers_ML.modifiers.forEach((mod) => {
                    mod.editMode_ML = editMode;
                });
            }
        });
    }
    
    const isVinDuplicated = useCallback((currentVehicle) => {
        const vehicles = _.get(endorsementVM, 'lobData.personalAuto.coverables.vehicles.children', []);
        const vehicleVin = currentVehicle.vin;
        const vehiclesWithTheSameVin = vehicles ? vehicles.filter(
            (x) => !_.isUndefined(x.vin.value) && x.vin.value === vehicleVin.value
        ) : [];
        return vehiclesWithTheSameVin.length >= 2;
    }, [endorsementVM]);

    const checkForAddlInterests = (selectedAlerts) => {
        vehiclesVM.forEach((veh) => {
            if (_.isNil(veh.makeAddlInfoReq_Ext.value)) {
                veh.value.makeAddlInfoReq_Ext = true;
            }
            if (isFinancedOrLeased(veh)) {
                if (veh.additionalInterests.value.length < 1) {
                    const errorMessage = translator(messages.updateFieldOrAddInterestedParty);
                    if (selectedAlerts.findIndex((alert) => alert.message === errorMessage) === -1) {
                        selectedAlerts.push({type: 'error', message: errorMessage, block: veh.value.publicID});
                        addErrorAlert(errorMessage, veh.value.publicID);
                    }
                } else {
                    removeAlertWithBlockID(selectedAlerts, veh.value.publicID);
                    clearBlockAlerts(veh.value.publicID);
                }
            } else {
                removeAlertWithBlockID(selectedAlerts, veh.value.publicID);
                clearBlockAlerts(veh.value.publicID);
            }
        });
    };

    const showVehicleOwnershipErrorMessages = (selectedAlerts) => {
        if (!!vehiclesVM && vehiclesVM.length > 0) {
            vehiclesVM.forEach((vehicleVM) => {
                let errorMsg = null;
                let errorName = null;

                if (hasPALeaseLoanCovButChangedOwnership(vehicleVM.value)) {
                    errorMsg = translator(messages.removeLeaseLoanGap);
                    errorName = 'removeLeaseLoanGap';
                }
                if (isFinanceWithoutPALeaseLoanCovButNoLienHolder(vehicleVM.value)) {
                    errorMsg = translator(messages.updateFieldOrAddInterestedParty);
                    errorName = 'noInterestedPartyInformation';
                }
                if (isLeaseButNoLessor(vehicleVM.value)) {
                    errorMsg = translator(messages.updateFieldOrAddInterestedParty);
                    errorName = 'leasedWithNoLeasingcompany';
                }
                if (isOwnedButWithLienHolderLessor(vehicleVM.value)) {
                    errorMsg = translator(messages.updateOwnedFinanceLeasedField);
                    errorName = 'updateOwnedFinanceLeasedField';
                }
                if (!!errorMsg && !!errorName) {
                    if (selectedAlerts.findIndex((alert) => alert.message === errorMsg) === -1) {
                        selectedAlerts.push({type: 'error', message: errorMsg, block: errorName});
                        addErrorAlert(errorMsg, errorName);
                    }
                } else {
                    removeAlertWithBlockID(selectedAlerts, errorName);
                    clearBlockAlerts(errorName);
                }
            });
        }
    };

    const checkIfRerateNeeded = (driverVM) => {
        driversRequiresProof
            .filter((x) => x.fixedId.value === driverVM.fixedId.value)
            .map((driver) => Object.assign(driver, {
                rerateForDriverNeeded: driver.requiresProof_ML.value
            }));
    };

    const checkIfVinsVerified = () => {
        return !vehiclesVM.some((vehicle) => {
            return (!isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicle) && !vehicle.isValidVin);
        });
    };

    const isPageValid = (selectedAlerts) => {
        checkForAddlInterests(selectedAlerts);
        showVehicleOwnershipErrorMessages(selectedAlerts);
        if (!(endorsementVM.aspects.valid && endorsementVM.aspects.subtreeValid) || !validateVehiclesSerialNumber(vehiclesVM)) {
            return false;
        }

        if (!validateSpouseEmail(selectedAlerts)) {
            return false;
        }

        if (selectedAlerts.length > 0) {
            window.scrollTo(0, 0);
            return false;
        }
        
        return true;
    };

    const checkAllRequiredVinsLookedUp = () => {
        return vehiclesVM.every((vehicle) => {
            return (isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicle) || !_.isNil(vehicle.isValidVin));
        });
    };

    const saveAndQuote = () => {
        const promise = EndorsementService.saveAndQuote(endorsementVM.value, authHeader)
        showProgressDialog(platformMessages.quotingPolicyChange, promise);
        promise.then((responseData) => {
            MLTagManagerService.sendFormStepPolicyChangeStep();
            endorsementVM.value = new PolicyChange(responseData);
            updateWizardData(wizardData);
            goNext();
        });
    };

    const savePremiumAndGoToNext = () => {
        const jsonData = {total: endorsementVM.value.totalCost};
        MLPortalStorageService.store(endorsementVM.value.jobID, null, ML_STORAGE_KEY_PREVIOUS_PREMIUM, JSON.stringify(jsonData), authHeader).then(()=>{
            saveAndQuote();
        });
    };

    const verifyVinsOnLoad = () => {
        const intervalPromise = new Promise((resolve, reject) => {
            let repetitions = 12;
            const vinInterval = setInterval(() => {
                if (checkAllRequiredVinsLookedUp()) {
                    clearInterval(vinInterval);
                    if (checkIfVinsVerified()) {
                        resolve();
                    } else {
                        reject();
                    }
                }

                repetitions -= 1;
                if (repetitions === 0) {
                    clearInterval(vinInterval);
                    reject();
                }
            }, 250);
        });    
        showProgressDialog(messages.verifyingVins, intervalPromise, true, 0);
        intervalPromise.then(() => {
            savePremiumAndGoToNext();
        }, () => {
            setHidePage(false)
        });
    };

    const verifyAndSkipPage = () => {
        const selectedAlerts = [];
        if (isPageValid(selectedAlerts) && selectedAlerts.length === 0) {
            if (!hasNamedNonOwner) {
                verifyVinsOnLoad();
            } else {
                savePremiumAndGoToNext();
            }
            return;
        }    
        setHidePage(false);
    };

    useEffect(() => {
        if (driversVM.length > 1) {
            driversVM.forEach((driver) => {
                if (driver.value.spouseID_ML) {
                    driver.spouse = driversVM.find((spouse) => spouse.person.publicID.value === driver.value.spouseID_ML).person;
                    if (driver.value.spouse) {
                        driver.value.spouse.storedEmailAddress_ML = driver.value.spouse.emailAddress_ML;
                    }
                }
            });
        }

        driversVM.forEach((driver) => {
            driver.requiredOnAddtlInfo_Ext = true;
            if (driver.value.spouse_ML) {
                driver.value.spouse_ML.storedEmailAddress_ML = driver.value.spouse_ML.emailAddress_ML;
                if (isInCO(addressVM)) {
                    driver.value.spouseEmailRequired_ML = true;
                }
            }
            if (isRatedDriver(driver) || isExcludedDriver(driver)) {
                driver.value.isLicenseNumberRequired_ML = true;
            }
            if (isExcludedDriver(driver) && (isInGA(addressVM) || isInUT(addressVM) || isInOR(addressVM))) {
                driver.value.excludedDriverEmailRequired_ML = true;
            }
            if (isPrimaryDriver(driver)) {
                driver.value.reEnterEmailRequired_ML = !_.isNil(primaryDriverEmail.value) && primaryDriverEmail.value !== '';
            }

            driver.editMode_ML = true;

            if (driver.requiresProof_ML.value === true) {
                if ((driver.requestingState_ML.value === null || typeof driver.requestingState_ML === 'undefined') && addressVM !== null) {
                    const stateExistsInTypeList = driver.requestingState_ML.aspects.availableValues.some((state) => {
                        return state.code === endorsementVM.lobData.personalAuto.policyAddress_ML.state.value.code;
                    });
                    driver.requestingState_ML = stateExistsInTypeList ? addressVM.state.value : null;
                }
            }
            if (isExcludedDriver(driver)) {
                driver.value.isAgeFirstLicensedRequired_ML = true;
            }
        });

        MLPublicContactsService.getMortgageeContacts(authHeader).then((data) => {
            setPotentialContacts(data);
        });

        setModifierEditMode(true);
        verifyAndSkipPage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNext = () => {
        clearAlerts();
        const selectedAlerts = [];
        if (isPageValid(selectedAlerts) && (checkIfVinsVerified() || vehiclesExistWithYearPre1981_ML()) && selectedAlerts.length === 0 && isComponentValid) {
            savePremiumAndGoToNext();
        } else {
            setShowErrors(true);
            window.scrollTo(0, 0);
        }
    };

    const getVehiclesOverrides = () => {
        const overrides = vehiclesVM.map((vehicleVM, index) => {
            return {
                [`vehicleCount${index}`]: {
                    content: translator(messages.vehicleX, { vehicleNumber: index+1 })
                },
                [`vehicleName${index}`]: {
                    content: (
                        <>
                            <strong>{ getVehicleYearMakeName(vehicleVM) }</strong> { getVehicleModelTrimName(vehicleVM) }
                        </>
                    )                    
                },
                [`vinContainer${index}`]: {
                    visible: !isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicleVM)
                },
                [`vinLookupComponent${index}`]: {
                    vehicleVM,
                    quoteID: endorsementVM.jobID,
                    path: `lobData.personalAuto.coverables.vehicles.children[${index}]`,
                    isVinDuplicated,
                    onValueChange: (value, path) => {
                        _.set(endorsementVM, path, value);
                        refreshData();
                    },
                    updateIsValidVin: (vinValidFlag) => {
                        vehicleVM.isValidVin = vinValidFlag;
                    },
                    index,
                    onValidate,
                    showValidationErrors: showErrors,
                    updateVehicleDetails: refreshData
                },
                [`serialNumberContainer${index}`]: {
                    visible: isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicleVM)
                },
                [`financeCategory${index}`]: {
                    value: _.get(vehicleVM, 'financeCategory_ML.value.code', 'nonOwned'),
                    onValueChange: (value) => {
                        if (value === 'nonOwned') {
                            _.set(vehicleVM, 'namedNonOwner_ML', true);
                            _.set(vehicleVM, 'financeCategory_ML', undefined);
                        } else {
                            _.set(vehicleVM, 'financeCategory_ML', value);
                            _.set(vehicleVM, 'namedNonOwner_ML', false);
                        }
                        refreshData();
                    }           
                },
                [`financeCategoryTooltipComponent${index}`]: {
                    tooltipLabel: messages.financeCategoryHelpTextTitleMap,
                    tooltipContent: messages.financeCategoryHelpTextMap
                },
                [`additionalInterestListContainer${index}`]: {
                    visible: isFinancedOrLeased(vehicleVM)
                },
                [`additionalInterestListComponent${index}`]: {
                    model: vehicleVM.additionalInterests,
                    updateViewModel: refreshData,
                    onValueChange: (value, path) => {
                        _.set(endorsementVM, path, value);
                        refreshData();
                    },
                    initialPotentialContact: potentialContacts,
                    path: `lobData.personalAuto.coverables.vehicles.children[${index}]`,
                    showValidationErrors: showErrors,
                    onValidate
                }
            }          
        });

        return Object.assign({}, ...overrides);
    };

    const resetFinancialCertificateFields = (driverVM) => {
        if (driverVM.requiresProof_ML.value === true) {
            driverVM.editMode_ML = true;
            if ((driverVM.requestingState_ML.value === null || typeof driverVM.requestingState_ML === 'undefined') && addressVM !== null) {
                const stateExistsInTypeList = driverVM.requestingState_ML.aspects.availableValues.some((state) => {
                    return state.code === endorsementVM.lobData.personalAuto.policyAddress_ML.state.value.code;
                });
                driverVM.requestingState_ML = stateExistsInTypeList ? addressVM.state.value : null;
            }
        } else {
            driverVM.caseNumber_ML.value = '';
            driverVM.cfreffDate_ML.value = MLLocalDateUtil.date2LocalDate(new Date());
            driverVM.requestingState_ML = null;
        }
    };

    const financialCertificateSelectionChanged = (driverVM) => {
        resetFinancialCertificateFields(driverVM);
        checkIfRerateNeeded(driverVM);
    };

    const getDriversOverrides = () => {
        const overrides = driversVM.map((driverVM, index) => {
            return {
                [`ratedDriverCount${index}`]: {
                    visible: isRatedDriver(driverVM),
                    content: translator(messages.driverX, { driverNumber: index+1})
                },
                [`excludedDriverCount${index}`]: {
                    visible: isExcludedDriver(driverVM),
                    content: translator(messages.excludedDriverX, { driverNumber: index+1-ratedAndHouseholdCount})
                },
                [`driverName${index}`]: {
                    visible: isRatedDriver(driverVM) || isExcludedDriver(driverVM),
                    content: (
                        <strong>
                            { driverVM.value.person.displayFullName_Ext }
                        </strong>
                    )
                },
                [`licenseNumberContainer${index}`]: {
                    visible: !isHouseholdDriver(driverVM)
                },
                [`licenseStateContainer${index}`]: {
                    visible: !isHouseholdDriver(driverVM)
                },
                [`licenseStateTooltip${index}`]: {
                    tooltipLabel: messages.licenseStateTooltipTitle,
                    tooltipContent: messages.licenseStateTooltip
                },
                [`ageFirstLicensedContainer${index}`]: {
                    visible: isExcludedDriver(driverVM) && driverVM.ageFirstLicensed_ML.aspects.required
                },
                [`requiresProofContainer${index}`]: {
                    visible: isRatedDriver(driverVM) && driverVM.requiresProof_ML.value
                },
                [`requiresProof${index}`]: {
                    onValueChange: (value, path) => {
                        _.set(endorsementVM, path, value);
                        financialCertificateSelectionChanged(driverVM);
                        refreshData();
                    }
                },
                [`requiresProofTrueContainer${index}`]: {
                    visible: driverVM.requiresProof_ML.value
                },
                [`reasonRequired${index}`]: {
                    visible: driverVM.reasonRequired_Ext.aspects.required,
                    availableValues: fetchORAvailableValues(driverVM)
                },
                [`caseNumber${index}`]: {
                    visible: driverVM.requestingState_ML.value
                },
                [`emailContainer${index}`]: {
                    visible: showDriverEmailAddress(driverVM)
                },
                [`spouseEmailContainer${index}`]: {
                    visible: showSpouseEmailAddress()
                },
                [`spouseEmail${index}`]: {
                    path: showSpouseEmailAddress() ? `lobData.personalAuto.coverables.drivers.children[${primaryDriverSpouseIndex}].person.emailAddress_ML` : undefined
                }
            }
        });

        return Object.assign({}, ...overrides);
    }

    const overrideProps = {
        pageContainer: {
            className: hidePage ? styles.displayNone : ''
        },
        vehiclesIterable: {
            data: vehiclesVM
        },
        driversIterable: {
            data: driversVM
        },
        nextDescription: {
            content: (
                <FormattedMessage 
                    {...messages.nextDesc}
                    values={{
                        continueWithYourChange: <strong>{ translator(messages.continueWithYourChange) }</strong>,
                    }}
                />
            )
        },
        ...getVehiclesOverrides(),
        ...getDriversOverrides()
    };

    const resolvers = {
        resolveComponentMap: {
            MLTooltipComponent
        },
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    }

    return (
        <WizardPage
            policyChangePageHeaderML={messages.additionalDetailsHeader}
            showCancel={false}
            showPrevious={false}
            showNext={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                model={endorsementVM}
                onModelChange={refreshData}
                onValidationChange={onValidate}
                showErrors={showErrors}
            />
        </WizardPage>
    );
}

AMPAdditionalInfoToPurchasePagePolChange.propTypes = wizardProps;
export default AMPAdditionalInfoToPurchasePagePolChange;